var _class, _descriptor;
function _initializerDefineProperty(e, i, r, l) { r && Object.defineProperty(e, i, { enumerable: r.enumerable, configurable: r.configurable, writable: r.writable, value: r.initializer ? r.initializer.call(l) : void 0 }); }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _applyDecoratedDescriptor(i, e, r, n, l) { var a = {}; return Object.keys(n).forEach(function (i) { a[i] = n[i]; }), a.enumerable = !!a.enumerable, a.configurable = !!a.configurable, ("value" in a || a.initializer) && (a.writable = !0), a = r.slice().reverse().reduce(function (r, n) { return n(i, e, r) || r; }, a), l && void 0 !== a.initializer && (a.value = a.initializer ? a.initializer.call(l) : void 0, a.initializer = void 0), void 0 === a.initializer ? (Object.defineProperty(i, e, a), null) : a; }
function _initializerWarningHelper(r, e) { throw Error("Decorating class property failed. Please ensure that transform-class-properties is enabled and runs after the decorators transform."); }
import Service, { inject as service } from '@ember/service';
import { set, get, action } from '@ember/object';
import { typeOf } from '@ember/utils';
import { assert } from '@ember/debug';
import { getOwner } from '@ember/application';
import { scheduleOnce } from '@ember/runloop';
import { addListener, removeListener, sendEvent } from '@ember/object/events';
import { setupRouter, whenRouteIdle } from 'ember-app-scheduler';
let ATTEMPTS = 0;
const MAX_ATTEMPTS = 100; // rAF runs every 16ms ideally, so 60x a second

let requestId;
let callbackRequestId;

/**
 * By default, we start checking to see if the document height is >= the last known `y` position
 * we want to scroll to.  This is important for content heavy pages that might try to scrollTo
 * before the content has painted
 *
 * @method tryScrollRecursively
 * @param {Function} fn
 * @param {Object} scrollHash
 * @param {Element} [element]
 * @param {string?} url
 * @void
 */
function tryScrollRecursively(fn, scrollHash, element, url) {
  let documentHeight;
  // read DOM outside of rAF
  if (element) {
    documentHeight = Math.max(element.scrollHeight, element.offsetHeight, element.clientHeight);
  } else {
    const {
      body,
      documentElement: html
    } = document;
    documentHeight = Math.max(body.scrollHeight, body.offsetHeight, html.clientHeight, html.scrollHeight, html.offsetHeight);
  }
  callbackRequestId = window.requestAnimationFrame(() => {
    if (url && url.indexOf('#') > -1) {
      const hashElement = document.getElementById(url.split('#').pop());
      if (hashElement) {
        scrollHash = {
          x: hashElement.offsetLeft,
          y: hashElement.offsetTop
        };
      }
    }
    // write DOM (scrollTo causes reflow)
    if (documentHeight >= scrollHash.y || ATTEMPTS >= MAX_ATTEMPTS) {
      ATTEMPTS = 0;
      fn.call(null, scrollHash.x, scrollHash.y);
    } else {
      ATTEMPTS++;
      tryScrollRecursively(fn, scrollHash, element, url);
    }
  });
}

// to prevent scheduleOnce calling multiple times, give it the same ref to this function
const CALLBACK = function (transition) {
  this.updateScrollPosition(transition);
};
let RouterScroll = (_class = class RouterScroll extends Service {
  get isFastBoot() {
    const fastboot = getOwner(this).lookup('service:fastboot');
    return fastboot ? fastboot.get('isFastBoot') : false;
  }
  constructor() {
    super(...arguments);

    // https://github.com/ember-app-scheduler/ember-app-scheduler/pull/773
    _initializerDefineProperty(this, "router", _descriptor, this);
    _defineProperty(this, "key", void 0);
    _defineProperty(this, "targetElement", void 0);
    _defineProperty(this, "scrollElement", 'window');
    _defineProperty(this, "isFirstLoad", true);
    _defineProperty(this, "preserveScrollPosition", false);
    // ember-app-scheduler properties
    _defineProperty(this, "scrollWhenIdle", false);
    _defineProperty(this, "scrollWhenAfterRender", false);
    setupRouter(this.router);
  }

  // eslint-disable-next-line ember/classic-decorator-hooks
  init(...args) {
    super.init(...args);
    this._loadConfig();
    set(this, 'scrollMap', {
      default: {
        x: 0,
        y: 0
      }
    });
    addListener(this.router, 'routeWillChange', this._routeWillChange);
    addListener(this.router, 'routeDidChange', this._routeDidChange);
  }
  willDestroy() {
    removeListener(this.router, 'routeWillChange', this._routeWillChange);
    removeListener(this.router, 'routeDidChange', this._routeDidChange);
    if (requestId) {
      window.cancelAnimationFrame(requestId);
    }
    if (callbackRequestId) {
      window.cancelAnimationFrame(callbackRequestId);
    }
    super.willDestroy(...arguments);
  }

  /**
   * Updates the scroll position
   * it will be a single transition
   * @method updateScrollPosition
   * @param {transition|transition[]} transition If before Ember 3.6, this will be an array of transitions, otherwise
   */
  updateScrollPosition(transition) {
    if (this.isFirstLoad) {
      this.unsetFirstLoad();
    }
    let scrollPosition = this.position;

    // If `preserveScrollPosition` was not set on the controller, attempt fallback to `preserveScrollPosition` which was set on the router service.
    let preserveScrollPosition = (transition.router.currentRouteInfos || []).some(routeInfo => routeInfo.route.controller.preserveScrollPosition) || this.preserveScrollPosition;
    if (!preserveScrollPosition) {
      const {
        scrollElement,
        targetElement,
        currentURL
      } = this;
      if (targetElement || 'window' === scrollElement) {
        tryScrollRecursively(window.scrollTo, scrollPosition, null, currentURL);
      } else if ('#' === scrollElement.charAt(0)) {
        const element = document.getElementById(scrollElement.substring(1));
        if (element) {
          let fn = (x, y) => {
            element.scrollLeft = x;
            element.scrollTop = y;
          };
          tryScrollRecursively(fn, scrollPosition, element, currentURL);
        }
      }
    }
    sendEvent(this, 'didScroll', transition);
  }
  _routeWillChange() {
    if (this.isFastBoot) {
      return;
    }
    this.update();
  }
  _routeDidChange(transition) {
    if (this.isFastBoot) {
      return;
    }
    const scrollWhenIdle = this.scrollWhenIdle;
    const scrollWhenAfterRender = this.scrollWhenAfterRender;
    if (!scrollWhenIdle && !scrollWhenAfterRender) {
      // out of the option, this happens on the tightest schedule
      scheduleOnce('render', this, CALLBACK, transition);
    } else if (scrollWhenAfterRender && !scrollWhenIdle) {
      // out of the option, this happens on the second tightest schedule
      scheduleOnce('afterRender', this, CALLBACK, transition);
    } else {
      whenRouteIdle().then(() => {
        this.updateScrollPosition(transition);
      });
    }
  }
  unsetFirstLoad() {
    set(this, 'isFirstLoad', false);
  }
  update() {
    if (this.isFastBoot || this.isFirstLoad) {
      return;
    }
    const scrollElement = this.scrollElement;
    const targetElement = this.targetElement;
    const scrollMap = this.scrollMap;
    const key = this.key;
    let x;
    let y;
    if (targetElement) {
      let element = document.querySelector(targetElement);
      if (element) {
        x = element.offsetLeft;
        y = element.offsetTop;

        // if we are looking to where to transition to next, we need to set the default to the position
        // of the targetElement on screen
        set(scrollMap, 'default', {
          x,
          y
        });
      }
    } else if ('window' === scrollElement) {
      x = window.scrollX;
      y = window.scrollY;
    } else if ('#' === scrollElement.charAt(0)) {
      let element = document.getElementById(scrollElement.substring(1));
      if (element) {
        x = element.scrollLeft;
        y = element.scrollTop;
      }
    }

    // only a `key` present after first load
    if (key && 'number' === typeOf(x) && 'number' === typeOf(y)) {
      set(scrollMap, key, {
        x,
        y
      });
    }
  }
  _loadConfig() {
    const config = getOwner(this).resolveRegistration('config:environment');
    if (config && config.routerScroll) {
      const scrollElement = config.routerScroll.scrollElement;
      const targetElement = config.routerScroll.targetElement;
      assert('You defined both scrollElement and targetElement in your config. We currently only support definining one of them', !(scrollElement && targetElement));
      if ('string' === typeOf(scrollElement)) {
        set(this, 'scrollElement', scrollElement);
      }
      if ('string' === typeOf(targetElement)) {
        set(this, 'targetElement', targetElement);
      }
      const {
        scrollWhenIdle = false,
        scrollWhenAfterRender = false
      } = config.routerScroll;
      set(this, 'scrollWhenIdle', scrollWhenIdle);
      set(this, 'scrollWhenAfterRender', scrollWhenAfterRender);
    }
  }
}, _descriptor = _applyDecoratedDescriptor(_class.prototype, "router", [service], {
  configurable: true,
  enumerable: true,
  writable: true,
  initializer: null
}), _applyDecoratedDescriptor(_class.prototype, "_routeWillChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "_routeWillChange"), _class.prototype), _applyDecoratedDescriptor(_class.prototype, "_routeDidChange", [action], Object.getOwnPropertyDescriptor(_class.prototype, "_routeDidChange"), _class.prototype), _class);
Object.defineProperty(RouterScroll.prototype, 'position', {
  configurable: true,
  get() {
    const scrollMap = this.scrollMap;
    const stateUuid = window.history.state?.uuid;
    set(this, 'key', stateUuid);
    const key = this.key || '-1';
    return get(scrollMap, key) || scrollMap.default;
  }
});
export default RouterScroll;